//src/pages/HistoricoPontos.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import {
  Download,
  FileClock,
  Filter,
  Search,
  ShieldCheck,
} from "lucide-react";

import "./HistoricoPontos.css";
import ComProtecao from "../components/ComProtecao";

function HistoricoPontosConteudo() {
  const [historico, setHistorico] = useState([]);
  const [saldo, setSaldo] = useState(null);

  const [filtros, setFiltros] = useState({
    usuario_id: "",
    dataInicio: "",
    dataFim: "",
    status: "",
    expiraBreve: false,
    nomeCliente: "",
    numeroPedido: "",
  });

  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 10;

  useEffect(() => {
    buscarHistorico();
  }, []);

  const buscarHistorico = async () => {
    try {
      const res = await axios.get("/historico-pontos", { params: filtros });

      setHistorico(Array.isArray(res.data) ? res.data : []);
      setPaginaAtual(1);

      if (filtros.usuario_id) {
        const saldoRes = await axios.get(`/clientes/${filtros.usuario_id}/pontos`);
        setSaldo(saldoRes.data);
      } else {
        setSaldo(null);
      }
    } catch (err) {
      console.error("Erro ao buscar histórico:", err);
      setHistorico([]);
      setSaldo(null);
    }
  };

  const exportarExcel = () => {
    const dados = historico.map((h) => ({
      Cliente: h.nome_usuario,
      Pontos: h.pontos,
      Motivo: h.motivo,
      Origem: h.origem,
      "Data de Crédito": h.data_credito
        ? format(new Date(h.data_credito), "dd/MM/yyyy")
        : "",
      "Expira em": h.data_expiracao
        ? format(new Date(h.data_expiracao), "dd/MM/yyyy")
        : "",
      Status: h.status,
    }));

    const planilha = XLSX.utils.json_to_sheet(dados);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, planilha, "Histórico de Pontos");
    XLSX.writeFile(wb, "historico-pontos.xlsx");
  };

  const historicoPaginado = historico.slice(
    (paginaAtual - 1) * itensPorPagina,
    paginaAtual * itensPorPagina
  );

  const totalPaginas = Math.max(1, Math.ceil(historico.length / itensPorPagina));

  const mudarPagina = (nova) => {
    if (nova >= 1 && nova <= totalPaginas) setPaginaAtual(nova);
  };

  const formatarPontos = (valor) =>
    Math.ceil(Number(valor || 0)).toLocaleString("pt-BR");

  const resumo = useMemo(() => {
    return historico.reduce(
      (acc, item) => {
        const pontos = Number(item.pontos || 0);

        if (pontos > 0) acc.creditos += pontos;
        if (pontos < 0) acc.debitos += Math.abs(pontos);

        acc.totalRegistros += 1;

        return acc;
      },
      { creditos: 0, debitos: 0, totalRegistros: 0 }
    );
  }, [historico]);

  const corStatus = (status) => {
    switch (status) {
      case "aprovado":
        return "verde";
      case "reservado":
        return "amarelo";
      case "resgate":
        return "roxo";
      case "rejeitado":
        return "vermelho";
      case "em análise":
        return "cinza";
      case "estorno":
        return "azul";
      case "conquista":
      case "campanha":
        return "laranja";
      default:
        return "cinza";
    }
  };

  const handleFiltro = (campo, valor) => {
    setFiltros((prev) => ({ ...prev, [campo]: valor }));
  };

  return (
    <div className="historicoPontosAdmin-page">
      <div className="historicoPontosAdmin-topbar">
        <div>
          <span className="historicoPontosAdmin-kicker">Área administrativa</span>
          <h2>Histórico de Pontos</h2>
          <p>
            Consulte créditos, reservas, resgates, campanhas, expirações e
            movimentações de pontos dos clientes.
          </p>
        </div>

        <button
          type="button"
          className="historicoPontosAdmin-btn secondary"
          onClick={exportarExcel}
        >
          <Download size={16} />
          Exportar Excel
        </button>
      </div>

      <div className="historicoPontosAdmin-cards">
        <div className="historicoPontosAdmin-card">
          <span>Registros filtrados</span>
          <strong>{formatarPontos(resumo.totalRegistros)}</strong>
        </div>

        <div className="historicoPontosAdmin-card positivo">
          <span>Créditos no filtro</span>
          <strong>{formatarPontos(resumo.creditos)} pts</strong>
        </div>

        <div className="historicoPontosAdmin-card negativo">
          <span>Débitos no filtro</span>
          <strong>{formatarPontos(resumo.debitos)} pts</strong>
        </div>

        <div className="historicoPontosAdmin-card info">
          <span>Cliente selecionado</span>
          <strong>{filtros.usuario_id || "Todos"}</strong>
        </div>
      </div>

      <div className="historicoPontosAdmin-filtros">
        <div className="historicoPontosAdmin-field small">
          <label>ID usuário</label>
          <input
            type="number"
            placeholder="Ex: 258"
            value={filtros.usuario_id}
            onChange={(e) => handleFiltro("usuario_id", e.target.value)}
          />
        </div>

        <div className="historicoPontosAdmin-field large">
          <label>Cliente</label>
          <div className="historicoPontosAdmin-inputIcon">
            <Search size={15} />
            <input
              type="text"
              placeholder="Nome ou e-mail"
              value={filtros.nomeCliente}
              onChange={(e) => handleFiltro("nomeCliente", e.target.value)}
            />
          </div>
        </div>

        <div className="historicoPontosAdmin-field">
          <label>Nº pedido</label>
          <input
            type="text"
            placeholder="Pedido"
            value={filtros.numeroPedido}
            onChange={(e) => handleFiltro("numeroPedido", e.target.value)}
          />
        </div>

        <div className="historicoPontosAdmin-field">
          <label>Data início</label>
          <input
            type="date"
            value={filtros.dataInicio}
            onChange={(e) => handleFiltro("dataInicio", e.target.value)}
          />
        </div>

        <div className="historicoPontosAdmin-field">
          <label>Data fim</label>
          <input
            type="date"
            value={filtros.dataFim}
            onChange={(e) => handleFiltro("dataFim", e.target.value)}
          />
        </div>

        <div className="historicoPontosAdmin-field">
          <label>Status</label>
          <select
            value={filtros.status}
            onChange={(e) => handleFiltro("status", e.target.value)}
          >
            <option value="">Todos</option>
            <option value="aprovado">Aprovado</option>
            <option value="rejeitado">Rejeitado</option>
            <option value="reservado">Reservado</option>
            <option value="em análise">Em Análise</option>
            <option value="campanha">Campanha</option>
            <option value="conquista">Conquista</option>
            <option value="resgate">Resgatado</option>
            <option value="estorno">Estorno</option>
          </select>
        </div>

        <label className="historicoPontosAdmin-check">
          <input
            type="checkbox"
            checked={filtros.expiraBreve}
            onChange={(e) => handleFiltro("expiraBreve", e.target.checked)}
          />
          Expira em breve
        </label>

        <button
          type="button"
          className="historicoPontosAdmin-btn primary"
          onClick={buscarHistorico}
        >
          <Filter size={16} />
          Filtrar
        </button>
      </div>

      {saldo && (
        <div className="historicoPontosAdmin-saldo">
          <div>
            <span>Total</span>
            <strong>{formatarPontos(saldo.pontos_totais)} pts</strong>
          </div>

          <div className="reservado">
            <span>Reservado</span>
            <strong>{formatarPontos(saldo.pontos_reservados)} pts</strong>
          </div>

          <div className="disponivel">
            <span>Disponível</span>
            <strong>{formatarPontos(saldo.pontos_disponiveis)} pts</strong>
          </div>
        </div>
      )}

      <div className="historicoPontosAdmin-tableCard">
        <div className="historicoPontosAdmin-tableHeader">
          <div>
            <h3>Movimentações</h3>
            <span>{historico.length.toLocaleString("pt-BR")} resultado(s)</span>
          </div>

          <ShieldCheck size={18} />
        </div>

        <div className="historicoPontosAdmin-tableWrapper">
          <table className="historicoPontosAdmin-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Pontos</th>
                <th>Motivo</th>
                <th>Origem</th>
                <th>Data</th>
                <th>Expira em</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {historicoPaginado.length > 0 ? (
                historicoPaginado.map((p, idx) => (
                  <tr key={`${p.origem || "mov"}-${idx}`}>
                    <td>
                      <div>
                        <strong>{p.nome_usuario || "—"}</strong>
                        <br />
                        <span>ID cliente: {p.usuario_id || "—"}</span>
                      </div>
                    </td>

                    <td
                      className={
                        Number(p.pontos || 0) < 0
                          ? "historicoPontosAdmin-pontos negativo"
                          : Number(p.pontos || 0) > 0
                          ? "historicoPontosAdmin-pontos positivo"
                          : "historicoPontosAdmin-pontos neutro"
                      }
                    >
                      {formatarPontos(p.pontos)} pts
                    </td>

                    <td className="historicoPontosAdmin-motivo">
                      {p.motivo || "—"}
                    </td>

                    <td>{p.origem || "—"}</td>

                    <td>
                      {p.data_credito
                        ? format(new Date(p.data_credito), "dd/MM/yyyy")
                        : "—"}
                    </td>

                    <td>
                      {p.data_expiracao
                        ? format(new Date(p.data_expiracao), "dd/MM/yyyy")
                        : "—"}
                    </td>

                    <td>
                      <span
                        className={`historicoPontosAdmin-status ${corStatus(
                          p.status
                        )}`}
                      >
                        {p.status || "—"}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="historicoPontosAdmin-empty">
                    <FileClock size={18} />
                    Nenhum ponto encontrado para os filtros selecionados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="historicoPontosAdmin-paginacao">
        <button onClick={() => mudarPagina(paginaAtual - 1)} disabled={paginaAtual === 1}>
          Anterior
        </button>

        <span>
          Página {paginaAtual} de {totalPaginas}
        </span>

        <button
          onClick={() => mudarPagina(paginaAtual + 1)}
          disabled={paginaAtual === totalPaginas}
        >
          Próxima
        </button>
      </div>
    </div>
  );
}

export default function HistoricoPontos() {
  return (
    <ComProtecao rota="historico-pontos" tipo="pode_ler">
      <HistoricoPontosConteudo />
    </ComProtecao>
  );
}